import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UseMutationResult } from '@tanstack/react-query';

import type { GenerateTextsPayload, GenerateTextsResponse } from '@/features/campaign-texts/types/campaign-texts.types';

import {
  defaultTextPicksForResult,
  pushBulkEditTextPicksToForm,
  reconcileTextPicks,
  type TextPickBatches,
} from '../shared/bulkEditAiTextPicks';
import type { CountryAiMode, CountryGenResult } from '../shared/multiCountryBulkEdit.types';
import { countryGenResultHasTexts } from '../shared/translateBulkEditCountryTexts';

import { useBulkEditAiTextRegen } from './useBulkEditAiTextRegen';
import { type BulkEditTranslateMutation, useBulkEditAiTextTranslationSync } from './useBulkEditAiTextTranslationSync';

type GenMut = UseMutationResult<GenerateTextsResponse, Error, GenerateTextsPayload>;

export function useBulkEditAiTextPicksSync(p: {
  countryKeys: string[];
  globalCountryPerDrop: string[];
  textMode: CountryAiMode;
  resultsByCountry: Record<string, CountryGenResult>;
  setResultsByCountry: Dispatch<SetStateAction<Record<string, CountryGenResult>>>;
  genTexts: GenMut;
  translateMutation: BulkEditTranslateMutation;
  syncPerDropSmsTexts?: (batches: string[][]) => void;
  syncSmsTextsByCountry?: (by: Record<string, string[]>) => void;
}) {
  const [textPicks, setTextPicks] = useState<Record<string, TextPickBatches>>({});
  const resultsRef = useRef(p.resultsByCountry);
  const textPicksRef = useRef(textPicks);
  resultsRef.current = p.resultsByCountry;
  textPicksRef.current = textPicks;

  const {
    translatingCountryKey,
    translatingBatch,
    translateCountryTexts,
    translateMissingInBatch,
  } = useBulkEditAiTextTranslationSync({
    resultsRef,
    translateMutation: p.translateMutation,
    setResultsByCountry: p.setResultsByCountry,
  });

  const { regenTarget, regenerateUnselectedInBatch } = useBulkEditAiTextRegen({
    resultsRef,
    textPicksRef,
    genTexts: p.genTexts,
    setResultsByCountry: p.setResultsByCountry,
    setTextPicks,
  });

  useEffect(() => {
    setTextPicks((prev) => reconcileTextPicks(prev, p.countryKeys, p.resultsByCountry));
  }, [p.resultsByCountry, p.countryKeys]);

  const hasAi = useMemo(
    () =>
      p.countryKeys.some((ck) =>
        countryGenResultHasTexts(p.resultsByCountry[ck] ?? { countryKey: ck, texts: [], senders: [] }),
      ),
    [p.countryKeys, p.resultsByCountry],
  );

  const pushDraft = useCallback(() => {
    pushBulkEditTextPicksToForm({
      hasAi,
      textMode: p.textMode,
      countryKeys: p.countryKeys,
      globalCountryPerDrop: p.globalCountryPerDrop,
      results: resultsRef.current,
      picks: textPicksRef.current,
      syncPerDropSmsTexts: p.syncPerDropSmsTexts,
      syncSmsTextsByCountry: p.syncSmsTextsByCountry,
    });
  }, [hasAi, p.textMode, p.countryKeys, p.globalCountryPerDrop, p.syncPerDropSmsTexts, p.syncSmsTextsByCountry]);

  useEffect(() => {
    pushDraft();
  }, [textPicks, p.resultsByCountry, pushDraft]);

  const toggleAiTextPick = useCallback((ck: string, bi: number, li: number) => {
    setTextPicks((prev) => {
      const r = resultsRef.current[ck];
      if (!r) return prev;
      const base = prev[ck] ?? defaultTextPicksForResult(r);
      return {
        ...prev,
        [ck]: base.map((row, i) => (i === bi ? row.map((v, j) => (j === li ? !v : v)) : row)),
      };
    });
  }, []);

  const setAllAiTextPicksForBatch = useCallback((ck: string, bi: number, val: boolean) => {
    setTextPicks((prev) => {
      const r = resultsRef.current[ck];
      if (!r) return prev;
      const base = prev[ck] ?? defaultTextPicksForResult(r);
      return { ...prev, [ck]: base.map((row, i) => (i === bi ? row.map(() => val) : row)) };
    });
  }, []);

  const setAllAiTextPicksForCountry = useCallback((ck: string, val: boolean) => {
    setTextPicks((prev) => {
      const r = resultsRef.current[ck];
      if (!r) return prev;
      return { ...prev, [ck]: defaultTextPicksForResult(r).map((row) => row.map(() => val)) };
    });
  }, []);

  const clearTextPicks = useCallback(() => setTextPicks({}), []);

  return {
    textPicks,
    translatingCountryKey,
    translatingBatch,
    regenTarget,
    toggleAiTextPick,
    setAllAiTextPicksForBatch,
    setAllAiTextPicksForCountry,
    regenerateUnselectedInBatch,
    translateCountryTexts,
    translateMissingInBatch,
    clearTextPicks,
  };
}
